import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import SearchBar from "./SearchBar";
import LanguageSwitcher from "./LanguageSwitcher";
import { useContent } from "../lib/content";

const primaryLinks = [
  { to: "/", label: "Home" },
  { to: "/gallery", label: "Portfolio" },
  { to: "/voices", label: "Voices of Humanity" },
  { to: "/about", label: "About" },
  { to: "/adanobi", label: "Book a Session" },
  { to: "/contact", label: "Contact" },
];

const moreLinks = [
  { to: "/echo-alliance", label: "Echo Alliance" },
  { to: "/journeys", label: "Journeys" },
  { to: "/travel", label: "Ghana Travel" },
  { to: "/heritage", label: "Heritage" },
  { to: "/eyes-of-hope", label: "Eyes of Hope" },
  { to: "/journal", label: "Journal" },
  { to: "/store", label: "Fine Art" },
  { to: "/museum", label: "Museum" },
  { to: "/map", label: "Humanity Map" },
  { to: "/oral-history", label: "Oral Histories" },
  { to: "/films", label: "Films" },
  { to: "/legacy", label: "Legacy" },
  { to: "/account", label: "Members" },
  { to: "/volunteer", label: "Volunteer" },
  { to: "/press", label: "Press" },
  { to: "/founders-circle", label: "Founder's Circle" },
  { to: "/philosophy", label: "Philosophy" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const location = useLocation();
  const logoImage = useContent("branding.logo_image", "");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isMoreActive = moreLinks.some((l) => location.pathname.startsWith(l.to) && l.to !== "/");

  return (
    <>
      <header
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#0a0908]/90 backdrop-blur-xl border-b border-white/5 py-4"
            : "bg-gradient-to-b from-[#0a0908]/70 to-transparent py-6"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-3 group"
          >
            {logoImage ? (
              <img
                src={logoImage}
                alt="Cowrieking logo"
                data-testid="brand-logo-image"
                className="w-10 h-10 object-cover rounded-sm"
              />
            ) : (
              <span className="w-9 h-9 border border-[#d4af37] flex items-center justify-center font-serif text-[#d4af37] text-lg italic">
                C
              </span>
            )}
            <div className="leading-none">
              <div className="font-serif text-lg tracking-wide text-[#f4f0ec]">
                Cowrieking
              </div>
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#d4af37] mt-1">
                Mershack Kabu Aklie
              </div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-7">
            {primaryLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[12px] font-medium tracking-[0.18em] uppercase transition-colors ${
                    isActive
                      ? "text-[#d4af37]"
                      : "text-[#a8a29e] hover:text-[#f4f0ec]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}

            {/* More dropdown */}
            <div className="relative" ref={moreRef}>
              <button
                data-testid="nav-more-button"
                onClick={() => setMoreOpen(!moreOpen)}
                aria-expanded={moreOpen}
                aria-haspopup="true"
                className={`flex items-center gap-1.5 text-[12px] font-medium tracking-[0.18em] uppercase transition-colors ${
                  isMoreActive || moreOpen ? "text-[#d4af37]" : "text-[#a8a29e] hover:text-[#f4f0ec]"
                }`}
              >
                More <ChevronDown size={13} className={`transition-transform ${moreOpen ? "rotate-180" : ""}`} />
              </button>
              {moreOpen && (
                <div
                  data-testid="nav-more-menu"
                  className="absolute right-0 top-full mt-4 w-[420px] bg-[#0d0c0a] border border-white/10 shadow-2xl p-6 grid grid-cols-2 gap-x-8 gap-y-3"
                >
                  {moreLinks.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      data-testid={`nav-more-${l.label.toLowerCase().replace(/\s+/g, "-").replace("'", "")}`}
                      className={({ isActive }) =>
                        `text-[12px] tracking-[0.12em] uppercase transition-colors ${
                          isActive ? "text-[#d4af37]" : "text-[#a8a29e] hover:text-[#f4f0ec]"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/eyes-of-hope#donate"
              data-testid="nav-donate-button"
              className="text-[11px] font-medium tracking-[0.18em] uppercase px-5 py-2.5 bg-[#d4af37] text-[#0a0908] hover:bg-[#c39f2f] transition-colors"
            >
              Donate
            </Link>
            <div className="flex items-center gap-4 pl-2 border-l border-white/10">
              <SearchBar />
              <LanguageSwitcher />
            </div>
          </nav>

          <div className="xl:hidden flex items-center gap-2 sm:gap-3">
            <Link
              to="/eyes-of-hope#donate"
              data-testid="mobile-nav-donate-button"
              className="text-[10px] sm:text-[11px] font-bold tracking-[0.18em] uppercase px-3.5 sm:px-4 py-2 bg-[#d4af37] text-[#0a0908] hover:bg-[#c39f2f] active:scale-95 transition-all"
              aria-label="Donate to Eyes of Hope Foundation"
            >
              Donate
            </Link>
            <SearchBar />
            <LanguageSwitcher />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              className="text-[#f4f0ec]"
              aria-label={open ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={open}
            >
              {open ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <div
        data-testid="mobile-menu-panel"
        className={`fixed inset-0 z-40 bg-[#0a0908] xl:hidden transition-all duration-500 overflow-y-auto ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="min-h-full flex flex-col justify-center px-8 pt-24 pb-16">
          <nav className="flex flex-col gap-4">
            {primaryLinks.map((l, i) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-serif text-3xl ${
                    isActive ? "text-[#d4af37]" : "text-[#f4f0ec]"
                  } hover:text-[#d4af37] transition-colors`
                }
                style={{ transitionDelay: `${i * 40}ms` }}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <p className="mt-10 mb-4 text-[10px] font-mono uppercase tracking-[0.3em] text-[#78716c]">More</p>
          <nav className="grid grid-cols-2 gap-x-6 gap-y-3">
            {moreLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-more-${l.label.toLowerCase().replace(/\s+/g, "-").replace("'", "")}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.12em] ${
                    isActive ? "text-[#d4af37]" : "text-[#a8a29e]"
                  } hover:text-[#d4af37] transition-colors`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <Link
            to="/eyes-of-hope#donate"
            data-testid="mobile-nav-donate"
            className="mt-10 inline-flex w-fit btn-primary"
          >
            Donate Now
          </Link>
        </div>
      </div>
    </>
  );
}
